import crawlPolicyData from "../data/crawl-policy-data.json";

const PRIVATE_CRAWL_PREFIXES = [
  "/account",
  "/cart",
  "/checkout",
  "/checkouts",
  "/orders",
  "/search",
] as const;

// Thin generated-detail prefixes that should be served but kept noindex,follow +
// out of the sitemap. Read ONLY through the Indexability Contract
// (`isThinNoindexPath` in src/lib/indexability.ts) so the sitemap filter and the
// page's robots header can never disagree.
//
// Empty as of the 2026-06-15 demand-led promotion: baby-names, compatibility, and
// lal-kitab-remedies detail pages each earn real GSC demand (40K / 250K / 0.6K
// impr/28d) and are already canonical + in DERIVED_SPECS, so suppressing them was
// destroying traffic. Add a prefix here ONLY for a cluster that is genuinely thin
// AND earns ~no impressions (verify against gsc_pages first) — that single edit
// then removes it from the sitemap AND stamps noindex, in lockstep.
export const WEAK_INDEXABLE_VALUE_DETAIL_PREFIXES: readonly string[] =
  crawlPolicyData.weakIndexableValueDetailPrefixes;

const QUERY_CANONICAL_EXEMPT_PREFIXES = [
  "/api",
  "/cart",
  "/checkout",
  "/checkouts",
  "/ops",
] as const;

export const LEGACY_CRAWL_CANONICAL_PATHS: Record<string, string> = {
  "/kundali/": "/free-kundali/",
  "/daily-rashifal/": "/rashifal/",
  "/today-rashifal/": "/rashifal/",
  "/pages/about/": "/about/",
  "/pages/about-us/": "/about/",
  "/pages/contact/": "/contact/",
  "/pages/contact-us/": "/contact/",
  "/policies/privacy-policy/": "/privacy/",
  "/policies/shipping-policy/": "/shipping-policy/",
  "/rashi/": "/rashi/mesh/",
  "/remedies/": "/upay/",
  "/hi/remedies/": "/hi/upay/",
  "/dosha/kaal-sarp-dosha/": "/kaal-sarp-dosha/",
  "/dosha/mangal-dosha/": "/mangal-dosha-calculator/",
  "/mangal-dosha/": "/mangal-dosha-calculator/",
  "/dosha/pitra-dosha/": "/pitra-dosha/",
  "/dosha/shani-dosha/": "/sade-sati/",
  "/dosha/rahu-dosha/": "/upay/rahu/",
  "/remedies/for-dosha/rahu_dosha/": "/upay/rahu/",
  "/dosha/ketu-dosha/": "/upay/ketu/",
  "/remedies/for-dosha/ketu_dosha/": "/upay/ketu/",
  "/hi/monthly-rashifal/": "/hi/aaj-ka-rashifal/",
  "/hi/muhurat/naamkaran/": "/hi/muhurat/",
  "/collections/frontpage/": "/collections/all/",
  "/collections/frontpage.atom": "/collections/all/",
  // /collections/ bare → canonical collections hub (same pattern as /collections/frontpage/)
  "/collections/": "/collections/all/",
  "/products/": "/collections/all/",
  "/mahadasha/saturn/": "/mahadasha/shani/",
  "/nakshatra/punarvasu/pada-4/": "/nakshatra/punarvasu/",
  "/nakshatra/purva-bhadrapada/pada-4/": "/nakshatra/purva-bhadrapada/",
  "/yoga/dhana/": "/yoga/",
  "/yoga/viparita-raja/": "/yoga/",
  "/blogs/news/baby-names-by-nakshatra-complete-guide-for-2026/": "/baby-names/",
  "/blogs/news/sade-sati-2026-which-rashis-are-affected-and-what-to-do/": "/sade-sati/",
  "/blogs/news/free-kundali-how-ai-is-making-vedic-astrology-accessible-t/": "/free-kundali/",
  // Old Shopify blog/page URLs that 404 but still earn GSC impressions (2026-06-03 audit).
  // Tool-topic articles → 301 to the canonical tool (the tool owns the intent; rebuilding
  // would cannibalise it). Full slugs — the prior truncated "-accessible-t" key never matched.
  "/blogs/news/free-kundali-how-ai-is-making-vedic-astrology-accessible-to-everyone/":
    "/free-kundali/",
  "/blogs/news/saturn-transit-in-aries-2026-effects-on-all-12-rashis/": "/saturn-transit/",
  "/blogs/news/how-to-find-your-moon-sign-rashi-by-date-of-birth/": "/rashi-calculator/",
  "/blogs/news/kaal-sarp-dosha-signs-effects-and-proven-remedies/": "/kaal-sarp-dosha/",
  "/blogs/news/kundali-matching-beyond-36-gun-milan-what-really-matters/": "/kundli-matching/",
  "/blogs/news/kundli-matching-for-marriage-gun-milan-explained/": "/kundli-matching/",
  "/blogs/news/mangal-dosha-meaning-effects-and-remedies/": "/mangal-dosha-calculator/",
  "/blogs/news/what-is-kundli-complete-guide-to-vedic-birth-chart/": "/free-kundali/",
  "/blogs/news/nakshatra-the-27-lunar-mansions-of-vedic-astrology/": "/nakshatra/",
  "/pages/terms-and-conditions/": "/terms/",
  "/pages/privacy-policy/": "/privacy/",
  "/pages/delete-account/": "/privacy/",
  // Transit pages migrated from year-in-slug → evergreen URLs (durable-URL SEO:
  // authority compounds, year lives in title/content via getFullYear + {year}).
  // Matches existing evergreen /saturn-transit/, /venus-transit/, /ketu-transit/.
  "/jupiter-transit-2026/": "/jupiter-transit/",
  "/rahu-transit-2026/": "/rahu-transit/",
  // kundali-milan / kundli-milan → canonical kundli-matching (110K/mo "kundali milan" searches)
  "/kundali-milan/": "/kundli-matching/",
  "/kundli-milan/": "/kundli-matching/",
  "/hi/kundali-milan/": "/hi/kundli-matching/",
  "/hi/kundli-milan/": "/hi/kundli-matching/",
  // /lal-kitab-remedies/ has no hub page (child pages /lal-kitab-remedies/[planet]/ exist);
  // point the bare path to the Lal Kitab parent so any external links get a 301 not a 404.
  "/lal-kitab-remedies/": "/lal-kitab/",
  // /hi/ bare path 301s to /hi/aaj-ka-rashifal/ (no Next.js page.tsx at hi root);
  // register the canonical so breadcrumbs + any legacy links get a proper redirect.
  "/hi/": "/hi/aaj-ka-rashifal/",
};

function canonicalPath(pathname: string) {
  if (pathname === "/") return "/";
  if ((pathname.split("/").pop() ?? "").includes(".")) return pathname;
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

function matchesPrefix(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function isDetailBelowPrefix(pathname: string, prefix: string) {
  const normalizedPrefix = canonicalPath(prefix);
  const normalizedPath = canonicalPath(pathname);
  return normalizedPath !== normalizedPrefix && normalizedPath.startsWith(normalizedPrefix);
}

export function isPrivateCrawlPath(pathname: string) {
  return PRIVATE_CRAWL_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

export function isWeakIndexableValuePath(pathname: string) {
  return WEAK_INDEXABLE_VALUE_DETAIL_PREFIXES.some((prefix) =>
    isDetailBelowPrefix(pathname, prefix)
  );
}

export function isQueryCanonicalizationExemptPath(pathname: string) {
  return QUERY_CANONICAL_EXEMPT_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

export function legacyCrawlCanonicalPath(pathname: string) {
  return (
    LEGACY_CRAWL_CANONICAL_PATHS[pathname] ??
    LEGACY_CRAWL_CANONICAL_PATHS[canonicalPath(pathname)] ??
    null
  );
}

export function legacyPolicyCanonicalPath(pathname: string) {
  const canonical = legacyCrawlCanonicalPath(pathname);
  return pathname.startsWith("/policies/") ? canonical : null;
}

export function shouldInjectRouteJsonLd(pathname: string) {
  const normalized = canonicalPath(pathname);
  return ![
    "/auth/",
    "/cart/",
    "/checkout/",
    "/checkouts/",
    "/friends/",
    "/get-app/",
    "/notifications/",
    "/ops/",
    "/orders/",
    "/search/",
  ].some((prefix) => normalized.startsWith(prefix));
}
